import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  ClipboardList, 
  Package, 
  Truck, 
  Users, 
  Settings, 
  X,
  BarChart3,
  Factory,
  Wrench,
  Fuel,
  Crown,
  ChevronDown,
  ChevronRight,
  Layers,
  FileText,
  ShoppingCart,
  History,
  TrendingUp,
  CircleOff,
  RotateCcw,
  Archive,
  PackageOpen,
  Activity,
  Download,
  ShieldAlert,
  ShieldCheck,
  Clock,
  CheckCircle2,
  Quote,
  Plus,
  Search,
  ScanLine,
  ArrowRightLeft,
  CalendarDays,
  BookOpen,
  Zap,
  Inbox,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Small count bubble shown next to nav items
const NavBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="ml-auto min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none px-1 flex-shrink-0">
      {count > 99 ? '99+' : count}
    </span>
  );
};

const Sidebar = ({ isOpen, onClose, user }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({
    production: false,
    inventory: false,
    maintenance: false,
    diesel: false
  });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [sideCounts, setSideCounts] = useState({});

  // Fetch sidebar badge counts on mount + every 60 seconds
  useEffect(() => {
    const fetchCounts = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/sidebar-counts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSideCounts(await res.json());
      } catch { /* silent fail */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  // Capture the beforeinstallprompt event
  useEffect(() => {
    // Detect iOS
    const iosDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandalone = window.navigator.standalone === true;
    setIsIOS(iosDevice && !isInStandalone);

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Detect if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstalled(true);
    }
  };

  // Check if user has permission for a module
  const hasPermission = (module, action = 'view') => {
    if (!user) return false;
    if (user?.is_super_admin) return true;
    if (!module) return true; // null module = always visible
    // Require explicit true — undefined/missing means no access
    return user?.permissions?.[module]?.[action] === true;
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const isMenuActive = (paths) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  // Check for impersonation mode
  const isImpersonating = localStorage.getItem('impersonating');

  const handleExitImpersonation = () => {
    const originalToken = localStorage.getItem('original_token');
    if (originalToken) {
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('original_token');
      localStorage.removeItem('impersonating');
      window.location.reload();
    }
  };

  const navItems = [
    { path: '/', icon: Home, label: 'Home', exact: true, module: null },
    { path: '/tasks', icon: ClipboardList, label: 'Tasks', module: 'tasks', badgeKey: 'tasks_open' },
  ];

  // Dropdown menus
  // Dropdown menus
  const productionSubmenu = hasPermission('production') ? [
    { path: '/production?create=1', icon: Plus, label: 'Add Trip', module: 'production', isAction: true },
    { path: '/production', icon: Factory, label: 'View Production', module: 'production' },
  ] : [];

  const inventoryStandaloneItems = [
    { path: '/inventory/indent-summary', icon: Archive, label: 'Indent Summary', module: 'purchase_orders' },
    { path: '/inventory/price-analytics', icon: TrendingUp, label: 'Price Analytics', module: 'analytics' },
  ];

  const purchaseOrdersSubmenu = [
    { path: '/inventory/purchase-orders/indent?create=1', icon: Plus, label: 'Create Purchase Order', module: 'purchase_orders', isAction: true },
    { path: '/inventory/purchase-orders/indent', icon: ClipboardList, label: 'Indent', module: 'purchase_orders', badgeKey: 'po_pending_indents' },
    { path: '/inventory/purchase-orders/pending', icon: Clock, label: 'Pending', module: 'purchase_orders', badgeKey: 'po_pending_delivery' },
    { path: '/inventory/purchase-orders/completed', icon: CheckCircle2, label: 'Completed', module: 'purchase_orders' },
    { path: '/inventory/purchase-orders/history', icon: History, label: 'Purchase History', module: 'purchase_orders' },
  ];

  const quotationsSubmenu = [
    { path: '/inventory/quotations/received', icon: Quote, label: 'Received Quotes', module: 'purchase_orders' },
    { path: '/inventory/quotations/analysis', icon: Search, label: 'Quote Search & Compare', module: 'purchase_orders' },
  ];

  const partsReleaseSubmenu = [
    { path: '/stores-queue', icon: Inbox, label: 'Stores Queue', module: 'parts_release', badgeKey: 'parts_release_pending' },
    { path: '/inventory/parts-release/new', icon: ClipboardList, label: 'New Requests', module: 'parts_release' },
    { path: '/inventory/parts-release/pending', icon: Clock, label: 'Pending Requests', module: 'parts_release', badgeKey: 'parts_release_pending' },
    { path: '/inventory/parts-release/completed', icon: CheckCircle2, label: 'Completed', module: 'parts_release' },
  ];

  const maintenanceSubmenu = [
    { path: '/quick-log', icon: Zap, label: 'Quick Log', module: 'maintenance', isAction: true },
    { path: '/maintenance/truck', icon: Truck, label: 'Truck Section', module: 'maintenance' },
    { path: '/maintenance/machinery', icon: Wrench, label: 'Machinery Section', module: 'maintenance' },
    { path: '/cannibalization', icon: ArrowRightLeft, label: 'Cannibalization', module: 'maintenance', badgeKey: 'cannibalization_pending' },
    { path: '/maintenance/owning-cost', icon: TrendingUp, label: 'Owning Cost', module: 'maintenance', adminOnly: true },
  ];

  const dieselSubmenu = [
    { path: '/diesel?create=1', icon: Plus, label: 'Add Diesel Entry', module: 'diesel', isAction: true },
    { path: '/diesel', icon: Fuel, label: 'Consumption', module: 'diesel' },
    { path: '/diesel/deletion-log', icon: ClipboardList, label: 'Deletion Log', module: 'diesel' },
  ];

  const bottomNavItems = [
    { path: '/assets', icon: Truck, label: 'Asset Management', module: 'assets' },
    { path: '/tyres', icon: RotateCcw, label: 'Tyre Management', module: 'assets' },
    { path: '/scrap', icon: CircleOff, label: 'Scrap', module: null },
    { path: '/analytics', icon: BarChart3, label: 'Analytics', module: 'analytics' },
    { path: '/reports', icon: FileText, label: 'Reports', module: null, adminOnly: true },
    { path: '/monthly-review', icon: CalendarDays, label: 'Monthly Review', module: null, adminOnly: true, badgeKey: 'monthly_review_pending' },
  ];

  const AUDIT_EMAILS = ['nitinj@lsnminerals.com', 'vishnu@lsnminerals.com'];
  const canAccessAuditLog = AUDIT_EMAILS.includes(user?.email);

  const adminItems = [
    { path: '/users', icon: Users, label: 'User Management', module: 'users', forDeptHead: true },
    { path: '/roles', icon: Settings, label: 'Role Management', module: 'roles', adminOnly: true },
    { path: '/approval-rights', icon: ShieldCheck, label: 'Approval Rights', module: 'roles', adminOnly: true },
    { path: '/activity-log', icon: Activity, label: 'Activity Log', module: 'activity_log' },
    { path: '/settings', icon: Settings, label: 'Settings', module: 'settings' },
    ...(canAccessAuditLog ? [{ path: '/admin/deletion-audit-log', icon: ShieldAlert, label: 'Deletion Audit Log', auditOnly: true }] : []),
  ];

  // Filter items based on permissions
  const visibleNavItems = navItems.filter(item => 
    item.module === null || hasPermission(item.module)
  );
  const visibleBottomItems = bottomNavItems.filter(item => {
    if (item.adminOnly && !user?.is_admin && !user?.is_super_admin) return false;
    return item.module === null || hasPermission(item.module);
  });
  const visibleDieselItems = hasPermission('diesel') ? dieselSubmenu : [];
  const hasAnyInventoryAccess = hasPermission('inventory') || hasPermission('purchase_orders') || hasPermission('parts_release') || hasPermission('analytics') || hasPermission('parts_directory');
  const visiblePurchaseOrderItems = hasPermission('purchase_orders') ? purchaseOrdersSubmenu : [];
  const visibleQuotationsItems = hasPermission('purchase_orders') ? quotationsSubmenu : [];
  const visiblePartsReleaseItems = hasPermission('parts_release') ? partsReleaseSubmenu : [];
  const visibleStandaloneItems = inventoryStandaloneItems.filter(item => hasPermission(item.module));
  const visibleMaintenanceItems = maintenanceSubmenu.filter(item => {
    if (item.adminOnly && !user?.is_admin) return false;
    return hasPermission(item.module);
  });

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-transparent.png" 
                alt="LSN Logo" 
                className="h-10 w-10 object-contain flex-shrink-0"
              />
              <span className="font-bold text-lg">LSN Minerals</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="close-sidebar"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-1">
              {/* Regular nav items */}
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium flex-1">{item.label}</span>
                  <NavBadge count={sideCounts[item.badgeKey]} />
                </NavLink>
              ))}

              {/* Production Dropdown */}
              {productionSubmenu.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleMenu('production')}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isMenuActive(['/production'])
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    data-testid="nav-production-dropdown"
                  >
                    <div className="flex items-center gap-3">
                      <Factory className="h-5 w-5" />
                      <span className="font-medium">Production</span>
                    </div>
                    {expandedMenus.production ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {expandedMenus.production && (
                    <div className="ml-4 mt-1 space-y-1">
                      {productionSubmenu.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.path === '/production'}
                          onClick={onClose}
                          className={({ isActive }) =>
                            item.isAction
                              ? `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-semibold text-amber-400 hover:bg-amber-500/10 hover:text-amber-300`
                              : `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                                  isActive ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                }`
                          }
                          data-testid={`nav-production-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Maintenance Dropdown */}
              {visibleMaintenanceItems.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleMenu('maintenance')}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isMenuActive(['/maintenance'])
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    data-testid="nav-maintenance-dropdown"
                  >
                    <div className="flex items-center gap-3">
                      <Wrench className="h-5 w-5" />
                      <span className="font-medium">Maintenance</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <NavBadge count={sideCounts.maintenance_open} />
                      {expandedMenus.maintenance ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  </button>
                  {expandedMenus.maintenance && (
                    <div className="ml-4 mt-1 space-y-1">
                      {visibleMaintenanceItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                              isActive
                                ? 'bg-slate-600 text-white'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`
                          }
                          data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{item.label}</span>
                          <NavBadge count={sideCounts[item.badgeKey]} />
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Store & Inventory Dropdown */}
              {hasAnyInventoryAccess && (
                <div>
                  <button
                    onClick={() => toggleMenu('inventory')}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isMenuActive(['/inventory'])
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    data-testid="nav-inventory-dropdown"
                  >
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5" />
                      <span className="font-medium">Store & Inventory</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <NavBadge count={
                        (sideCounts.parts_release_pending || 0) +
                        (sideCounts.po_pending_indents || 0) +
                        (sideCounts.po_pending_delivery || 0)
                      } />
                      {expandedMenus.inventory ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </button>

                  {expandedMenus.inventory && (
                    <div className="ml-3 mt-1 space-y-0.5">

                      {/* ── 1. SKU Management ── */}
                      {hasPermission('inventory') && (
                        <NavLink
                          to="/inventory"
                          end
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
                              isActive ? 'bg-slate-600 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                            }`
                          }
                          data-testid="nav-sku-management"
                        >
                          <Layers className="h-4 w-4 flex-shrink-0" />
                          <span className="font-bold text-sm">SKU Management</span>
                        </NavLink>
                      )}

                      {/* ── 1b. Parts List Directory ── */}
                      {hasPermission('parts_directory') && (
                        <NavLink
                          to="/inventory/parts-directory"
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
                              isActive ? 'bg-slate-600 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                            }`
                          }
                          data-testid="nav-parts-directory"
                        >
                          <BookOpen className="h-4 w-4 flex-shrink-0" />
                          <span className="font-bold text-sm">Parts Directory</span>
                        </NavLink>
                      )}

                      {/* ── 2. Parts Release ── */}
                      {visiblePartsReleaseItems.length > 0 && (
                        <div className="pt-1">
                          <div className="flex items-center gap-2 px-3 py-1.5">
                            <FileText className="h-4 w-4 text-slate-300 flex-shrink-0" />
                            <span className="font-bold text-sm text-slate-200">Parts Release</span>
                          </div>
                          <div className="ml-3 space-y-0.5">
                            {visiblePartsReleaseItems.map((item) => (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                className={({ isActive }) =>
                                  `flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                                    isActive ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                  }`
                                }
                                data-testid={`nav-parts-${item.label.toLowerCase().replace(/ /g, '-')}`}
                              >
                                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                                <NavBadge count={sideCounts[item.badgeKey]} />
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── 3. Purchase Orders ── */}
                      {visiblePurchaseOrderItems.length > 0 && (
                        <div className="pt-1">
                          <div className="flex items-center gap-2 px-3 py-1.5">
                            <ShoppingCart className="h-4 w-4 text-slate-300 flex-shrink-0" />
                            <span className="font-bold text-sm text-slate-200">Purchase Orders</span>
                          </div>
                          <div className="ml-3 space-y-0.5">
                            {visiblePurchaseOrderItems.map((item) => (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                className={({ isActive }) =>
                                  item.isAction
                                    ? `flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm font-semibold text-amber-400 hover:bg-amber-500/10 hover:text-amber-300`
                                    : `flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                                        isActive ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                      }`
                                }
                                data-testid={`nav-po-${item.label.toLowerCase().replace(/ /g, '-')}`}
                              >
                                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                                {!item.isAction && <NavBadge count={sideCounts[item.badgeKey]} />}
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── 4. Quotations ── */}
                      {visibleQuotationsItems.length > 0 && (
                        <div className="pt-1">
                          <div className="flex items-center gap-2 px-3 py-1.5">
                            <Quote className="h-4 w-4 text-slate-300 flex-shrink-0" />
                            <span className="font-bold text-sm text-slate-200">Quotations</span>
                          </div>
                          <div className="ml-3 space-y-0.5">
                            {visibleQuotationsItems.map((item) => (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                className={({ isActive }) =>
                                  `flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                                    isActive ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                  }`
                                }
                                data-testid={`nav-quotations-${item.label.toLowerCase().replace(/ /g, '-')}`}
                              >
                                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── 5. Indent Summary + Price Analytics ── */}
                      {visibleStandaloneItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors mt-1 ${
                              isActive ? 'bg-slate-600 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                            }`
                          }
                          data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="font-bold text-sm">{item.label}</span>
                        </NavLink>
                      ))}

                    </div>
                  )}
                </div>
              )}

              {/* Diesel Dropdown */}
              {visibleDieselItems.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleMenu('diesel')}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isMenuActive(['/diesel'])
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    data-testid="nav-diesel-dropdown"
                  >
                    <div className="flex items-center gap-3">
                      <Fuel className="h-5 w-5" />
                      <span className="font-medium">Diesel</span>
                    </div>
                    {expandedMenus.diesel ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedMenus.diesel && (
                    <div className="ml-4 mt-1 space-y-1">
                      {visibleDieselItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.path === '/diesel'}
                          onClick={onClose}
                          className={({ isActive }) =>
                            item.isAction
                              ? `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-semibold text-amber-400 hover:bg-amber-500/10 hover:text-amber-300`
                              : `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                                  isActive ? 'bg-slate-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                }`
                          }
                          data-testid={`nav-diesel-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Bottom nav items */}
              {visibleBottomItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium flex-1">{item.label}</span>
                  <NavBadge count={sideCounts[item.badgeKey]} />
                </NavLink>
              ))}
            </div>

          {/* Impersonation Banner */}
          {isImpersonating && (
            <div className="mx-3 mb-2 rounded-lg bg-amber-500/20 border border-amber-500/40 px-3 py-2.5">
              <p className="text-xs text-amber-300 font-semibold">Viewing as</p>
              <p className="text-sm text-amber-100 font-medium truncate">
                {JSON.parse(isImpersonating).name}
              </p>
              <button
                onClick={handleExitImpersonation}
                data-testid="exit-impersonation-btn"
                className="mt-1.5 w-full text-xs bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-1 font-medium transition-colors"
              >
                Exit — Back to Super Admin
              </button>
            </div>
          )}

          {/* Admin Section */}
          {(user?.is_admin || user?.permissions?.can_manage_users) && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Admin
              </p>
              <div className="px-3 space-y-1">
                {adminItems.filter(item => {
                  if (item.auditOnly) return canAccessAuditLog;
                  if (item.adminOnly) return user?.is_admin; // Role Management: admins only
                  if (item.forDeptHead) return true; // User Management: dept heads too
                  if (item.module === 'activity_log') return hasPermission('activity_log');
                  return user?.is_admin; // settings etc: admins only
                }).map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-slate-700 text-white'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}

                {/* Scanned Cards Archive — super admin only */}
                {user?.is_super_admin && (
                  <NavLink
                    to="/archived-scans"
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-slate-700 text-white'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                    data-testid="nav-archived-scans"
                  >
                    <ScanLine className="h-5 w-5" />
                    <span className="font-medium">Scanned Cards</span>
                  </NavLink>
                )}
              </div>
            </div>
          )}
          </nav>

          {/* Install App Button — Android */}
          {installPrompt && !isInstalled && (
            <div className="px-4 pb-3">
              <button
                onClick={handleInstall}
                data-testid="install-app-btn"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-colors"
              >
                <Download className="h-4 w-4 flex-shrink-0" />
                <div className="text-left min-w-0">
                  <p className="text-xs font-semibold leading-tight">Install App</p>
                  <p className="text-xs opacity-70 leading-tight truncate">Add to Home Screen</p>
                </div>
              </button>
            </div>
          )}

          {/* iOS Install Hint */}
          {isIOS && !isInstalled && (
            <div className="px-4 pb-3">
              <button
                onClick={() => setShowIOSHint(h => !h)}
                data-testid="ios-install-hint-btn"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors"
              >
                <Download className="h-4 w-4 flex-shrink-0" />
                <div className="text-left min-w-0">
                  <p className="text-xs font-semibold leading-tight">Install on iPhone</p>
                  <p className="text-xs opacity-70 leading-tight">Tap for instructions</p>
                </div>
              </button>
              {showIOSHint && (
                <div className="mt-2 bg-slate-800 border border-slate-600 rounded-lg p-3 text-xs text-slate-300 space-y-1.5">
                  <p className="font-semibold text-white">Add to Home Screen:</p>
                  <p>1. Tap the <strong>Share</strong> button in Safari (box with arrow up)</p>
                  <p>2. Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                  <p>3. Tap <strong>Add</strong> — done!</p>
                </div>
              )}
            </div>
          )}

          {/* User Info */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center">
                <span className="text-sm font-semibold">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{user?.full_name}</p>
                  {user?.is_super_admin && (
                    <Crown className="h-3 w-3 text-amber-400" title="Super Admin" />
                  )}
                </div>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                {user?.role && user?.role !== 'custom' && (
                  <Badge className="mt-1 text-xs bg-slate-700 text-slate-300">
                    {user.role.replace('_', ' ')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
